// ============================================================
// ArcReview.jsx — Architectural Review Committee.
//
// Plans arrive at arc@harvus.com (or import+arc@harvus.com), land against a lot, and are measured
// against the community's adopted standards. Backed by /api/arc/* — every
// write is Admin server-side, so the affordances here are cosmetic.
//
// THREE THINGS THIS SCREEN IS CAREFUL ABOUT, all of them about not overstating
// what the machine did:
//
//  • "NOT SHOWN" IS NOT "DOES NOT MEET". A required rule the drawings are
//    silent on renders amber and reads as a request for information; a rule the
//    drawings contradict renders red. Painting them the same colour accuses an
//    applicant of a violation they did not commit, and is the fastest way to
//    make a committee stop reading the report.
//
//  • A DRAFT RULE SET IS NOT THE STANDARDS. A draft is shown as a proposal with
//    an Activate button, never as though it were in force — nothing is reviewed
//    against it until a person adopts it.
//
//  • THE REPORT IS A WORKING PAPER UNTIL SOMEBODY RELEASES IT, and the caveat
//    the API sends is rendered verbatim on every report rather than being
//    reworded here. That is unchanged now the review fires on arrival — what
//    became automatic is the READING, never the releasing.
//
//  • THE SCREEN SAYS WHETHER ANYTHING IS COMING. A committee that has been told
//    reviews are automatic reads an empty report as a broken feature, so the
//    community header states plainly whether automatic review is on, that a
//    submission settles for a few minutes first, and that one with no lot waits
//    for a person — the three reasons a report is not there yet.
// ============================================================
import { useState, useMemo, useEffect } from 'react';
import { B, space, font, radius, weight } from '../constants/brand';
import { api } from '../api/client';
import { useApi } from '../hooks/useApi';
import { fmt } from '../utils/format';
import {
  Card, Btn, Spinner, ErrorMsg, Badge, Modal, ModalFooter, Tabs, Table,
  Input, Select, Textarea, KpiCard, useToast,
} from '../components/ui';
import { InlineSearchPicker, useDealPickerItems } from '../components/tasks/TaskComponents';
import { matchesAllWords } from '../utils/wordSearch';
import { isOffice, officeOnlineViewerUrl } from '../utils/docViewer';
import { renderPdfPages } from '../utils/pdfPages';

// Result vocabulary. `unclear` is deliberately amber and worded as a question,
// never as a finding against the applicant — see the header.
const RESULT_STYLE = {
  fail:    { label: 'Does not meet', color: B.red,    bg: '#fdeceb' },
  unclear: { label: 'Not shown',     color: '#8a6100', bg: '#fdf4e0' },
  na:      { label: 'Not applicable',color: B.textMd, bg: B.surfaceMuted },
  pass:    { label: 'Meets',         color: '#1c6b40', bg: '#e8f6ee' },
};
const VERDICT_STYLE = {
  'non-compliant': { label: 'Not compliant',          color: B.red },
  'needs-info':    { label: 'More information needed', color: '#8a6100' },
  'compliant':     { label: 'Appears compliant',       color: '#1c6b40' },
};

const Pill = ({ tone, children }) => (
  <span style={{
    display: 'inline-block', padding: '2px 9px', borderRadius: radius.pill,
    background: tone.bg, color: tone.color, fontSize: font.xs, fontWeight: weight.semibold,
    whiteSpace: 'nowrap',
  }}>{children}</span>
);

const Empty = ({ children }) => (
  <div style={{ padding: '38px 16px', textAlign: 'center', color: B.textMd, fontSize: font.base }}>{children}</div>
);

// ── The compliance report ──────────────────────────────────────────────────
// Built to be READ, not scrolled: a real review returns ~130 findings, and
// giving each the same full card buried the two that matter under ninety
// "Not shown" rows. So the report opens on the most serious group only, the
// tally doubles as the filter, rows are grouped by the standard's category, and
// the covenant quote sits behind a disclosure. Every piece of the old card is
// still one click away — nothing is dropped, only ordered.
const GROUP_ORDER = ['fail', 'unclear', 'pass', 'na'];
const ACTION_LABEL = { fail: 'To fix', unclear: 'To provide' };

function firstNonEmpty(counts) {
  return GROUP_ORDER.find(k => (counts[k] || 0) > 0) || 'fail';
}

function matchesQuery(f, q) {
  if (!q) return true;
  const hay = [f.code, f.title, f.category, f.requirement, f.evidence, f.required, f.shown, f.note, f.pageRef, f.sourceRef]
    .filter(Boolean).join(' ').toLowerCase();
  return q.toLowerCase().split(/\s+/).filter(Boolean).every(t => hay.includes(t));
}

// A finding as three labelled lines — what the rule demands, what the plans
// give, and the one thing to do — rather than four stacked paragraphs. The
// short Required / Plans show lines come from the review (migration 238); an
// older finding has none and falls back to the full standard and evidence.
// The covenant text, the full evidence and the rule code sit behind "Source".
const Line = ({ label, children, strong }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '92px 1fr', gap: space.sm, fontSize: font.sm, lineHeight: 1.45, marginTop: 3 }}>
    <span style={{ color: B.textLt, fontSize: font.xs, textTransform: 'uppercase', letterSpacing: 0.4, paddingTop: 2 }}>{label}</span>
    <span style={{ color: B.text, fontWeight: strong ? weight.semibold : weight.regular }}>{children}</span>
  </div>
);

function FindingRow({ f, defaultOpen, index }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const [source, setSource] = useState(false);
  const tone = RESULT_STYLE[f.result] || RESULT_STYLE.unclear;
  const action = ACTION_LABEL[f.result];
  const hasShort = !!(f.required || f.shown);
  return (
    <div id={f.findingID ? `finding-${f.findingID}` : undefined}
      style={{ borderBottom: `1px solid ${B.divider}`, borderLeft: `3px solid ${open ? tone.color : 'transparent'}` }}>
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}
        style={{
          all: 'unset', boxSizing: 'border-box', cursor: 'pointer', width: '100%',
          display: 'grid', gridTemplateColumns: '10px 1fr auto', gap: space.sm,
          alignItems: 'baseline', padding: `${space.sm}px ${space.sm}px`,
        }}>
        <span style={{ width: 8, height: 8, borderRadius: 4, background: tone.color, display: 'inline-block', transform: 'translateY(-1px)' }} />
        <span style={{ minWidth: 0 }}>
          {index != null && <span style={{ color: B.textLt, marginRight: 6 }}>{index}.</span>}
          <span style={{ fontWeight: weight.semibold, color: B.text }}>{f.title}</span>
          {f.severity !== 'required' && <span style={{ marginLeft: 8, fontSize: font.xs, color: B.textLt }}>{f.severity}</span>}
          {f.checkResult && <span style={{ marginLeft: 8 }}><Pill tone={RESULT_STYLE.unclear}>Check — may be “Not shown”</Pill></span>}
          {!open && f.note && action && (
            <span style={{ display: 'block', fontSize: font.sm, color: B.textMd, marginTop: 2 }}>{action}: {f.note}</span>
          )}
        </span>
        <span style={{ display: 'flex', gap: space.sm, alignItems: 'baseline', whiteSpace: 'nowrap' }}>
          {f.pageRef && <span style={{ fontSize: font.xs, color: B.textMd }}>{f.pageRef}</span>}
          <span style={{ fontSize: font.xs, color: B.textLt }}>{open ? '▾' : '▸'}</span>
        </span>
      </button>
      {open && (
        <div style={{ padding: `0 ${space.sm}px ${space.md}px ${space.sm + 18}px` }}>
          <Line label="Required">{f.required || f.requirement || '—'}</Line>
          <Line label="Plans show">{f.shown || f.evidence || 'Nothing on the submitted sheets addresses this.'}</Line>
          {f.note && action && <Line label={action} strong>{f.note}</Line>}
          {f.checkResult && (
            <div style={{ fontSize: font.xs, color: RESULT_STYLE.unclear.color, background: RESULT_STYLE.unclear.bg, borderRadius: radius.lg, padding: `${space.xs}px ${space.sm}px`, marginTop: space.sm }}>
              {f.checkResult}
            </div>
          )}
          <button type="button" onClick={() => setSource(v => !v)}
            style={{ all: 'unset', cursor: 'pointer', fontSize: font.xs, color: B.navy, marginTop: space.sm }}>
            {source ? '▾' : '▸'} Source
          </button>
          {source && (
            <div style={{ marginTop: space.xs }}>
              {hasShort && f.requirement && <Line label="Standard">{f.requirement}</Line>}
              {hasShort && f.evidence && <Line label="Read">{f.evidence}</Line>}
              {/* The governing text, so a finding can be checked against the
                  covenant rather than taken on trust. */}
              {f.sourceQuote && (
                <div style={{
                  fontSize: font.xs, color: B.textMd, marginTop: space.sm, paddingLeft: space.sm,
                  borderLeft: `2px solid ${B.divider}`, fontStyle: 'italic',
                }}>
                  &ldquo;{f.sourceQuote}&rdquo;{f.sourceRef ? ` — ${f.sourceRef}` : ''}
                </div>
              )}
              <div style={{ fontSize: font.xs, color: B.textLt, marginTop: space.xs, fontFamily: 'ui-monospace, monospace' }}>{f.code}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// The answer to "why is this not compliant", directly under the verdict: one
// line per failure, each jumping to its detail. Nothing here is computed — the
// lines are the review's own short fields, or the standard's title alone.
function FailureSummary({ items, onJump }) {
  if (!items.length) return null;
  return (
    <div style={{ border: `1px solid ${RESULT_STYLE.fail.bg}`, background: '#fffafa', borderRadius: radius.xl, padding: space.md, marginBottom: space.md }}>
      <div style={{ fontWeight: weight.semibold, color: RESULT_STYLE.fail.color, marginBottom: space.xs }}>
        {items.length} standard{items.length === 1 ? '' : 's'} not met
      </div>
      <ol style={{ margin: 0, paddingLeft: 20 }}>
        {items.map(f => (
          <li key={f.findingID || f.code} style={{ fontSize: font.sm, color: B.text, margin: '3px 0', lineHeight: 1.45 }}>
            <button type="button" onClick={() => onJump(f)}
              style={{ all: 'unset', cursor: 'pointer', fontWeight: weight.semibold, color: B.text, textDecoration: 'underline', textDecorationColor: B.border }}>
              {f.title}
            </button>
            {(f.required || f.shown) && (
              <span style={{ color: B.textMd }}>
                {f.required ? ` — needs ${f.required}` : ''}{f.shown ? ` · plans show ${f.shown}` : ''}
              </span>
            )}
            {f.pageRef && <span style={{ color: B.textLt }}> · {f.pageRef}</span>}
            {f.checkResult && <span style={{ marginLeft: 6 }}><Pill tone={RESULT_STYLE.unclear}>check</Pill></span>}
          </li>
        ))}
      </ol>
    </div>
  );
}

// One paint callout read off an element. A matched colour shows its swatch and
// the published value the picture was drawn from; anything else says why the
// picture had to guess, with the colours it could have meant.
const PAINT_NOTE = { ambiguous: 'Not exact', conflict: 'Number and name disagree', 'not-found': 'Not in the catalog', 'no-code': 'No colour number' };
function Swatch({ hex }) {
  return <span title={hex} style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 3, background: hex, border: `1px solid ${B.border}`, verticalAlign: 'middle' }} />;
}
function PaintColour({ c }) {
  if (c.status === 'matched') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: font.xs, color: B.textMd, marginTop: 2 }}>
        <Swatch hex={c.hex} />
        <span>{c.brand} {c.code}{c.name ? ` ${c.name}` : ''} · {c.hex}</span>
      </div>
    );
  }
  return (
    <div style={{ fontSize: font.xs, color: '#8a6100', marginTop: 2 }}>
      ⚠ {PAINT_NOTE[c.status] || 'Unresolved'}: “{c.written}” — {c.message}. Drawn from the words, not an exact colour.
      {(c.candidates || []).length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 2, color: B.textMd }}>
          {c.candidates.map((x, k) => (
            <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Swatch hex={x.hex} />{x.code}{x.name ? ` ${x.name}` : ''}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// One named colour from the applicant's documents and where its value came
// from. The source tier is spelled out, because a manufacturer's own page and a
// reference site's approximation are different claims.
const MATERIAL_FLAG = {
  'no-manufacturer': 'No manufacturer named — not looked up',
  ambiguous: 'Not exact — not looked up',
  conflict: 'Number and name disagree — not looked up',
  'not-found': 'Not found on manufacturer or trusted reference sites',
  skipped: 'Not looked up',
};
function MaterialRow({ m }) {
  const what = [m.manufacturer, m.product, m.colour].filter(Boolean).join(' ');
  const ok = m.status === 'found' || m.status === 'catalog';
  const tierColor = m.status === 'catalog' || m.tier === 'manufacturer' || m.tier === 'manual' ? B.green : '#8a6100';
  return (
    <div style={{ fontSize: font.sm, padding: '4px 0', borderBottom: `1px solid ${B.divider}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {ok && m.hex && <Swatch hex={m.hex} />}
        <span style={{ fontWeight: weight.semibold, color: B.text }}>{m.element}</span>
        <span style={{ color: B.text }}>— {what}</span>
        {m.document && <span style={{ color: B.textLt, fontSize: font.xs }}>· {m.document}</span>}
      </div>
      {ok ? (
        <div style={{ fontSize: font.xs, color: B.textMd, marginTop: 2 }}>
          <span style={{ color: tierColor, fontWeight: weight.semibold }}>{m.label || (m.status === 'catalog' ? 'Paint catalog' : 'Found')}</span>
          {m.name && <span> · {m.name}</span>}
          {m.hex && <span> · {m.hex}</span>}
          {!m.hex && <span> · no published colour value — drawn from the description</span>}
          {m.description && <div>{m.description}</div>}
          {m.url && <div><a href={m.url} target="_blank" rel="noreferrer" style={{ color: B.navy }}>Source</a></div>}
        </div>
      ) : (
        <div style={{ fontSize: font.xs, color: '#8a6100', marginTop: 2 }}>
          ⚠ {MATERIAL_FLAG[m.status] || 'Not resolved'}{(m.reason || m.message) ? ` — ${m.reason || m.message}` : ''}. Drawn from the words only.
          {(m.candidates || []).length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 2, color: B.textMd }}>
              {m.candidates.map((x, k) => <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Swatch hex={x.hex} />{x.code}{x.name ? ` ${x.name}` : ''}</span>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Front-elevation rendering ──
// Two halves on screen, deliberately side by side: the picture (easy to look at,
// an illustration) and the list read off the plans (the part to check). The
// caveat comes from the API verbatim.
// What the drawing was made from. Without this a picture that ignored the
// plans looks the same as one drawn from them.
function RenderingSources({ refs }) {
  const named = refs.elevationNamed;
  const photos = refs.photos || [];
  const skipped = refs.skipped || [];
  return (
    <div style={{ fontSize: font.xs, color: B.textMd, marginTop: space.sm, lineHeight: 1.5 }}>
      <div>
        <strong>Drawn from:</strong>{' '}
        {refs.elevation ? `${refs.elevation.fileName}, page ${refs.elevation.page}${refs.elevation.where ? ` (${refs.elevation.where})` : ''}` : null}
        {refs.elevation && photos.length ? ' · ' : null}
        {photos.length ? photos.map(p => `${p.fileName} (${p.view === 'unclassified' ? 'photo' : `${p.view} photo`})`).join(', ') : null}
        {!refs.elevation && !photos.length ? 'the written description only' : null}
      </div>
      {!refs.elevation && (
        <div style={{ color: B.amber }}>
          {named
            ? `The plans put the front elevation on page ${named.page} of ${named.document}, but that page was not sent from the browser.`
            : refs.pagesSent ? 'No page of the plans was identified as the front elevation.' : 'The plan pages were not sent, so the drawing could not follow them.'}
        </div>
      )}
      {skipped.length > 0 && (
        <div>Not sent: {skipped.map(x => `${x.fileName} (${x.reason || x.view})`).join(', ')}</div>
      )}
    </div>
  );
}

function RenderingPanel({ reviewId, canWrite, startCollapsed }) {
  const { toast } = useToast();
  // Closed by default when the report has failures: the drawing is an
  // illustration, and it must not push the findings off the first screen.
  const [collapsed, setCollapsed] = useState(!!startCollapsed);
  const [starting, setStarting] = useState(false);
  const [showSpec, setShowSpec] = useState(false);
  const { data, loading, reload } = useApi(() => api.get(`arc/reviews/${reviewId}/rendering`).catch(e => ({ unavailableReason: e.message })), [reviewId]);
  const r = data && data.rendering;
  const pending = starting || (r && r.status === 'pending');

  useEffect(() => {
    if (!pending) return undefined;
    const t = setInterval(() => reload(), 5000);
    return () => clearInterval(t);
  }, [pending, reload]);

  const [prep, setPrep] = useState(null);
  const start = async () => {
    setStarting(true);
    // The plan PDFs are turned into page images HERE, in the browser, so the
    // illustrator can see the drawing (the API cannot rasterize a PDF). A PDF
    // that cannot be read costs the drawing its reference, not the rendering.
    const pages = [];
    try {
      setPrep('Reading the plan sheets…');
      const src = await api.get(`arc/reviews/${reviewId}/rendering/sources`);
      const limit = (src && src.maxPages) || 30;
      for (const pdf of (src && src.pdfs) || []) {
        if (pages.length >= limit) break;
        setPrep(`Reading ${pdf.fileName}…`);
        const { sasUrl } = await api.get(`documents/${pdf.documentID}/download-url`);
        const rendered = await renderPdfPages(sasUrl, { maxPages: limit - pages.length });
        rendered.forEach(pg => pages.push({ documentId: pdf.documentID, ...pg }));
      }
    } catch (e) {
      toast.error(`Could not read the plan PDF in the browser (${e.message || e}). Drawing from the photos and the written description only.`);
    }
    setPrep(null);
    // The request draws inline and routinely outlives the ~45 s edge, so its
    // own outcome is not trusted: the poll reads the row.
    api.post(`arc/reviews/${reviewId}/rendering`, { pages }, { timeout: 300000 })
      .catch(e => { if (e && e.status && e.status < 500) toast.error(e.message || 'Could not start the rendering'); })
      .finally(() => { setStarting(false); reload(); });
    setTimeout(() => reload(), 1500);
  };

  if (loading && !data) return null;
  if (data && data.unavailableReason) return null;

  return (
    <div style={{ border: `1px solid ${B.divider}`, borderRadius: radius.xl, padding: space.md, marginBottom: space.lg, background: B.surface }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: space.sm, flexWrap: 'wrap' }}>
        <button type="button" onClick={() => setCollapsed(c => !c)} aria-expanded={!collapsed}
          style={{ all: 'unset', cursor: 'pointer', fontWeight: weight.semibold, color: B.text }}>
          {collapsed ? '▸' : '▾'} Front elevation
        </button>
        <span style={{ fontSize: font.xs, color: B.textLt }}>ChatGPT illustration from the plans</span>
        <div style={{ flex: 1 }} />
        {canWrite && (
          <Btn size="sm" variant={r ? 'secondary' : 'primary'} onClick={start} loading={pending} disabled={pending}>
            {pending ? 'Drawing…' : r ? 'Redraw' : 'Draw front elevation'}
          </Btn>
        )}
      </div>

      {!collapsed && prep && <div style={{ fontSize: font.sm, color: B.textMd, marginTop: space.sm }}>{prep}</div>}
      {!collapsed && !prep && pending && r && r.message && <div style={{ fontSize: font.sm, color: B.textMd, marginTop: space.sm }}>{r.message} This takes a minute or two.</div>}
      {!collapsed && !pending && r && r.references && <RenderingSources refs={r.references} />}
      {!collapsed && !r && !pending && (
        <div style={{ fontSize: font.sm, color: B.textMd, marginTop: space.xs }}>
          {canWrite ? 'Reads the materials and colours off the front elevation, then draws it.' : 'No rendering yet.'}
        </div>
      )}
      {!collapsed && r && r.status === 'error' && <div style={{ marginTop: space.sm }}><ErrorMsg msg={r.message || 'The rendering failed.'} /></div>}

      {!collapsed && r && r.status === 'ready' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: space.md, marginTop: space.md }}>
          <div>
            {r.imageUrl
              ? <a href={r.imageUrl} target="_blank" rel="noreferrer"><img src={r.imageUrl} alt="Illustration of the front elevation" style={{ width: '100%', borderRadius: radius.lg, display: 'block', border: `1px solid ${B.divider}` }} /></a>
              : <Empty>The image could not be loaded.</Empty>}
            <div style={{ fontSize: font.xs, color: B.textMd, marginTop: space.xs }}>{data.caveat}</div>
          </div>
          <div>
            <div style={{ fontSize: font.xs, color: B.textLt, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: space.xs }}>Read off the plans</div>
            {(r.items || []).slice(0, showSpec ? undefined : 8).map((it, i) => (
              <div key={i} style={{ fontSize: font.sm, padding: '4px 0', borderBottom: `1px solid ${B.divider}` }}>
                <span style={{ fontWeight: weight.semibold, color: B.text }}>{it.label}</span>
                <span style={{ color: /not specified/i.test(it.spec) ? '#8a6100' : B.text }}> — {it.spec}</span>
                {it.sheet && <span style={{ color: B.textLt, fontSize: font.xs }}> · {it.sheet}</span>}
                {(it.colours || []).map((c, j) => <PaintColour key={j} c={c} />)}
              </div>
            ))}
            {Array.isArray(r.differences) && (
              <>
                <div style={{ fontSize: font.xs, color: B.textLt, textTransform: 'uppercase', letterSpacing: 0.4, margin: `${space.md}px 0 ${space.xs}px` }}>Plans vs. as built (photo)</div>
                {r.differences.length === 0
                  ? <div style={{ fontSize: font.sm, color: B.textMd }}>The photo matches the plans on everything visible.</div>
                  : r.differences.map((d, i) => (
                    <div key={i} style={{ fontSize: font.sm, padding: '4px 0', borderBottom: `1px solid ${B.divider}` }}>
                      <div style={{ fontWeight: weight.semibold, color: '#8a6100' }}>{d.element}</div>
                      <div style={{ color: B.text }}><span style={{ color: B.textLt }}>Plans:</span> {d.plans || '—'}</div>
                      <div style={{ color: B.text }}><span style={{ color: B.textLt }}>Built:</span> {d.asBuilt || '—'}{d.photo && <span style={{ color: B.textLt, fontSize: font.xs }}> · {d.photo}</span>}</div>
                    </div>
                  ))}
                <div style={{ fontSize: font.xs, color: B.textMd, marginTop: space.xs }}>Read off the photo by AI — check against the photo before relying on it.</div>
              </>
            )}
            {(r.materials || []).length > 0 && (
              <>
                <div style={{ fontSize: font.xs, color: B.textLt, textTransform: 'uppercase', letterSpacing: 0.4, margin: `${space.md}px 0 ${space.xs}px` }}>Colours named in the documents</div>
                {r.materials.map((m, i) => <MaterialRow key={i} m={m} />)}
              </>
            )}
            {(r.items || []).length > 8 && (
              <button type="button" onClick={() => setShowSpec(v => !v)}
                style={{ all: 'unset', cursor: 'pointer', color: B.navy, fontSize: font.sm, marginTop: space.xs }}>
                {showSpec ? 'Show fewer' : `Show all ${r.items.length}`}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ReportModal({ reviewId, onClose, onChanged, canWrite }) {
  const { toast } = useToast();
  const [releasing, setReleasing] = useState(false);
  const [view, setView] = useState(null);       // a result key, or 'all'
  const [q, setQ] = useState('');
  const [requiredOnly, setRequiredOnly] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [jumpTo, setJumpTo] = useState(null);
  const { data, loading, error, reload } = useApi(() => api.get(`arc/reviews/${reviewId}`), [reviewId]);

  const findings = (data && data.report && data.report.findings) || [];
  const counts = (data && data.report && data.report.counts) || {};
  const active = view || firstNonEmpty(counts);

  const visible = useMemo(() => findings.filter(f =>
    (active === 'all' || f.result === active)
    && (!requiredOnly || f.severity === 'required')
    && matchesQuery(f, q)), [findings, active, requiredOnly, q]);

  // A jump from the summary switches to the failures and scrolls once the row
  // has rendered.
  useEffect(() => {
    if (!jumpTo) return;
    const el = document.getElementById(`finding-${jumpTo}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setJumpTo(null);
  }, [jumpTo, active]);

  // Grouped by result (only matters for 'all'), then by the standard's
  // category — and only in the 'all' view: under one result a heading per
  // category is mostly "PORCHES · 1", a heading per item.
  const sections = useMemo(() => {
    const out = [];
    for (const res of GROUP_ORDER) {
      const inRes = visible.filter(f => f.result === res);
      if (!inRes.length) continue;
      if (active !== 'all') { out.push({ res, cats: [['', inRes]] }); continue; }
      const byCat = new Map();
      for (const f of inRes) {
        const c = f.category || 'Other';
        if (!byCat.has(c)) byCat.set(c, []);
        byCat.get(c).push(f);
      }
      out.push({ res, cats: [...byCat.entries()] });
    }
    return out;
  }, [visible, active]);

  if (loading) return <Modal title="Compliance report" onClose={onClose} width={960}><Spinner /></Modal>;
  if (error || !data) return <Modal title="Compliance report" onClose={onClose} width={960}><ErrorMsg msg={String(error || 'not found')} /></Modal>;

  const { review, report } = data;
  const verdict = report.verdict ? VERDICT_STYLE[report.verdict] : { label: 'Not reviewed', color: B.textMd };

  const release = async () => {
    setReleasing(true);
    try {
      await api.post(`arc/reviews/${reviewId}/release`, {});
      toast.success('Released — the committee can now send this out.');
      reload(); onChanged && onChanged();
    } catch (e) {
      // Shown verbatim: the API's refusal names the reason.
      toast.error(e.message || 'Could not release the report');
    } finally { setReleasing(false); }
  };

  // What the applicant has to fix or supply, as plain text for an email.
  const copyRequests = async () => {
    const lines = [];
    for (const res of ['fail', 'unclear']) {
      const items = findings.filter(f => f.result === res && f.severity === 'required' && f.note);
      if (!items.length) continue;
      lines.push(res === 'fail' ? 'Items that do not meet the standards:' : 'Information still needed:');
      items.forEach((f, i) => lines.push(`${i + 1}. ${f.title}${f.pageRef ? ` (${f.pageRef})` : ''} — ${f.note}`));
      lines.push('');
    }
    try {
      await navigator.clipboard.writeText(lines.join('\n').trim() || 'Nothing outstanding.');
      toast.success('Copied the list of items for the applicant.');
    } catch { toast.error('Could not copy to the clipboard'); }
  };

  const tabs = [...GROUP_ORDER.filter(k => (counts[k] || 0) > 0), 'all'];

  return (
    <Modal title="Compliance report" onClose={onClose} width={960}>
      {/* Header: what, where, and whether it has gone anywhere. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.md, alignItems: 'baseline' }}>
        <div style={{ fontSize: font.xl, fontWeight: weight.bold, color: verdict.color }}>{verdict.label}</div>
        <div style={{ color: B.textMd, fontSize: font.sm }}>
          {[report.lotCode && `Lot ${report.lotCode}`, report.lotAddress, report.ruleSetVersion != null && `standards v${report.ruleSetVersion}`,
            report.reviewedUTC && `reviewed ${fmt.date(report.reviewedUTC)}`].filter(Boolean).join(' · ')}
        </div>
        <div style={{ flex: 1 }} />
        {review.releasedUTC
          ? <Badge color="#1c6b40">Released {fmt.date(review.releasedUTC)}</Badge>
          : <Badge color={B.textMd}>Draft — not sent</Badge>}
      </div>
      {/* Verbatim from the API — a reader who takes the verdict as the
          committee's decision is the failure this whole feature has. */}
      <div style={{ fontSize: font.xs, color: B.textMd, margin: `${space.xs}px 0 ${space.md}px` }}>{report.caveat}</div>

      {review.status === 'error' && <ErrorMsg msg={review.message || 'The review did not finish.'} />}

      <FailureSummary items={findings.filter(f => f.result === 'fail')}
        onJump={f => { setRequiredOnly(false); setQ(''); setView('fail'); setJumpTo(f.findingID); }} />
      {report.toCheck > 0 && (
        <div style={{ fontSize: font.sm, color: RESULT_STYLE.unclear.color, marginTop: -space.xs, marginBottom: space.md }}>
          {report.toCheck} of these ask the applicant to <em>provide</em> something rather than change the design — check whether {report.toCheck === 1 ? 'it is' : 'they are'} really “Not shown” before sending.
        </div>
      )}

      {report.summary && (
        <div style={{ background: B.surfaceMuted, borderRadius: radius.xl, padding: space.md, marginBottom: space.md }}>
          <button type="button" onClick={() => setShowSummary(v => !v)}
            style={{ all: 'unset', cursor: 'pointer', fontWeight: weight.semibold, color: B.text, fontSize: font.sm }}>
            {showSummary ? '▾' : '▸'} Summary for the homeowner
          </button>
          {showSummary && <div style={{ whiteSpace: 'pre-wrap', color: B.text, fontSize: font.sm, lineHeight: 1.55, marginTop: space.xs }}>{report.summary}</div>}
        </div>
      )}

      {review.status === 'ready' && <RenderingPanel reviewId={reviewId} canWrite={canWrite} startCollapsed={(counts.fail || 0) > 0} />}

      {findings.length === 0 && <Empty>This review produced no findings.</Empty>}

      {findings.length > 0 && (
        <>
          {/* The tally IS the filter. */}
          <div role="tablist" style={{ display: 'flex', gap: space.xs, flexWrap: 'wrap', marginBottom: space.sm }}>
            {tabs.map(k => {
              const on = active === k;
              const tone = k === 'all' ? { color: B.navy, bg: B.surfaceMuted } : RESULT_STYLE[k];
              const label = k === 'all' ? 'All' : RESULT_STYLE[k].label;
              const n = k === 'all' ? findings.length : counts[k];
              return (
                <button key={k} type="button" role="tab" aria-selected={on} onClick={() => setView(k)}
                  style={{
                    all: 'unset', cursor: 'pointer', padding: '6px 12px', borderRadius: radius.pill,
                    fontSize: font.sm, fontWeight: weight.semibold,
                    background: on ? tone.color : tone.bg, color: on ? '#fff' : tone.color,
                  }}>
                  {label} <span style={{ opacity: 0.85, fontWeight: weight.regular }}>{n}</span>
                </button>
              );
            })}
          </div>

          <div style={{ display: 'flex', gap: space.sm, alignItems: 'center', flexWrap: 'wrap', marginBottom: space.sm }}>
            <div style={{ flex: '1 1 240px' }}>
              <Input value={q} onChange={setQ} placeholder="Search standards, sheets, notes…" />
            </div>
            <label style={{ fontSize: font.sm, color: B.textMd, display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" checked={requiredOnly} onChange={e => setRequiredOnly(e.target.checked)} /> Required only
            </label>
            {(counts.fail || counts.unclear) ? <Btn size="sm" variant="secondary" onClick={copyRequests}>Copy items for applicant</Btn> : null}
          </div>

          {visible.length === 0 && <Empty>No findings match.</Empty>}

          {sections.map(sec => (
            <div key={sec.res} style={{ marginBottom: space.md }}>
              {active === 'all' && (
                <div style={{ marginTop: space.md, marginBottom: space.xs }}>
                  <Pill tone={RESULT_STYLE[sec.res]}>{RESULT_STYLE[sec.res].label}</Pill>
                </div>
              )}
              {sec.cats.map(([cat, items]) => (
                <div key={cat} style={{ marginBottom: space.sm }}>
                  {sec.cats.length > 1 && (
                    <div style={{ fontSize: font.xs, color: B.textLt, textTransform: 'uppercase', letterSpacing: 0.4, padding: `${space.sm}px ${space.xs}px 2px` }}>
                      {cat} <span style={{ color: B.textLt }}>· {items.length}</span>
                    </div>
                  )}
                  {items.map((f, i) => (
                    <FindingRow key={f.findingID || f.code} f={f} defaultOpen={f.result === 'fail'}
                      index={active === 'fail' ? i + 1 : null} />
                  ))}
                </div>
              ))}
            </div>
          ))}
        </>
      )}

      <ModalFooter>
        <Btn variant="secondary" onClick={onClose}>Close</Btn>
        {canWrite && review.status === 'ready' && !review.releasedUTC && (
          <Btn onClick={release} loading={releasing}>Release to the committee</Btn>
        )}
      </ModalFooter>
    </Modal>
  );
}

// ── The lot file ───────────────────────────────────────────────────────────
// Re-reads a captured email from the ARC mailbox and lands any attachment its
// submission is missing. Plans emailed while the intake's document write was
// broken were captured with no files, and a scan skips a message it has seen —
// this is the way back for one submission.
async function refetchAttachments(submissionID, toast) {
  try {
    const out = await api.post(`arc/submissions/${submissionID}/attachments/refetch`, {}, { timeout: 60000 });
    const n = out.documentCount || 0;
    if (n) toast.success(`Fetched ${n} file${n === 1 ? '' : 's'} from the original email.`);
    else toast.info(out.reason || 'No new files on the original email.');
    return n;
  } catch (e) {
    toast.error(e.message || 'Could not fetch the attachments');
    return 0;
  }
}

function LotFileModal({ lotId, onClose, canWrite, onOpenReport }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(null);
  const { data, loading, error, reload } = useApi(() => api.get(`arc/lots/${lotId}`), [lotId]);

  if (loading) return <Modal title="Lot file" onClose={onClose} width={860}><Spinner /></Modal>;
  if (error || !data) return <Modal title="Lot file" onClose={onClose} width={860}><ErrorMsg msg={String(error || 'not found')} /></Modal>;

  const { lot, submissions, documents } = data;

  // The review reads every plan sheet and calls the model inline — minutes, not
  // seconds — and the Static Web Apps edge closes the request at ~45 s while the
  // function carries on. So the POST is fired on a long budget and the LOT FILE
  // is the outcome: a lost response (timeout, bodyless 5xx) is followed by
  // polling until the submission's latest review leaves 'pending'. The
  // request's own error is surfaced only when no review ever started.
  const runReview = async (submissionID) => {
    setBusy(submissionID);
    const before = submissions.find(x => x.submissionID === submissionID)?.lastReviewID || null;
    let lost = null;
    try {
      const out = await api.post(`arc/submissions/${submissionID}/review`, {}, { timeout: 10 * 60 * 1000 });
      if (out.inProgress) toast.info('A review of this submission is already running — waiting for it to finish.');
      else if (out.error) { toast.error(out.error); setBusy(null); reload(); return; }
      else { toast.success(`Reviewed — ${out.findingCount} requirements checked.`); setBusy(null); reload(); return; }
    } catch (e) {
      if (e.status && e.status < 500) { toast.error(e.message || 'The review could not be run'); setBusy(null); return; }
      lost = e;
    }
    // Poll the lot file for the outcome (inProgress, or the response was lost).
    const deadline = Date.now() + 15 * 60 * 1000;
    let sawStart = false;
    while (Date.now() < deadline) {
      await new Promise(r => setTimeout(r, 5000));
      let file;
      try { file = await api.get(`arc/lots/${lotId}`, { timeout: 20000 }); } catch { continue; }
      const s = (file.submissions || []).find(x => x.submissionID === submissionID);
      if (!s) break;
      if (s.reviewRunning) { sawStart = true; continue; }
      if (s.lastReviewID && (s.lastReviewID !== before || sawStart)) {
        if (s.lastReviewStatus === 'ready') toast.success(s.lastReviewMessage || 'Review finished.');
        else toast.error(s.lastReviewMessage || 'The review did not finish.');
        setBusy(null); reload(); return;
      }
      // No new review and nothing running: the request never started one.
      if (!sawStart && Date.now() > deadline - 14 * 60 * 1000) {
        toast.error(lost?.message || 'The review could not be started');
        setBusy(null); reload(); return;
      }
    }
    toast.info('The review is still running — reopen the lot file later to see the report.');
    setBusy(null); reload();
  };

  return (
    <Modal title={`Lot ${lot.lotCode}${lot.address ? ` — ${lot.address}` : ''}`} onClose={onClose} width={860}>
      <div style={{ display: 'flex', gap: space.xl, flexWrap: 'wrap', marginBottom: space.lg, fontSize: font.sm }}>
        {[['Owner', lot.ownerName], ['Owner email', lot.ownerEmail], ['Builder', lot.builderName],
          ['Phase', lot.phase], ['Acres', lot.acres == null ? null : fmt.number(lot.acres)]]
          .filter(([, v]) => v).map(([k, v]) => (
            <div key={k}><div style={{ color: B.textLt, fontSize: font.xs }}>{k}</div><div style={{ color: B.text }}>{v}</div></div>
          ))}
      </div>
      {lot.notes && <div style={{ marginBottom: space.lg, color: B.textMd, fontSize: font.sm, whiteSpace: 'pre-wrap' }}>{lot.notes}</div>}

      <div style={{ fontWeight: weight.semibold, marginBottom: space.sm, color: B.text }}>Submissions</div>
      {submissions.length === 0 && <Empty>Nothing has been submitted for this lot yet.</Empty>}
      {submissions.map(s => {
        const v = s.lastVerdict ? VERDICT_STYLE[s.lastVerdict] : null;
        const isDup = s.status === 'duplicate';
        const setDuplicate = async (duplicate) => {
          setBusy(`dup:${s.submissionID}`);
          try {
            await api.post(`arc/submissions/${s.submissionID}/duplicate`, { duplicate });
            toast.success(duplicate ? 'Marked as a duplicate — it will not be reviewed.' : 'Unmarked — this submission can be reviewed again.');
          } catch (e) { toast.error(e.message || 'Could not update the submission'); }
          setBusy(null); reload();
        };
        return (
          <div key={s.submissionID} style={{
            border: `1px solid ${B.border}`, borderRadius: radius.lg, padding: space.md, marginBottom: space.sm,
            background: isDup ? B.surfaceMuted : undefined, opacity: isDup ? 0.75 : 1,
          }}>
            <div style={{ display: 'flex', gap: space.sm, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: weight.semibold, color: B.text }}>{s.subject || '(no subject)'}</span>
              {v && <Badge color={v.color}>{v.label}</Badge>}
              {s.lastReleasedUTC && <Badge color="#1c6b40">Released</Badge>}
              {s.lastReviewStatus === 'error' && <Badge color={B.red}>Review failed</Badge>}
              {s.reviewRunning && <Badge color={B.amber}>Review running</Badge>}
              {isDup && <Badge color={B.textMd}>Duplicate — not processed</Badge>}
            </div>
            <div style={{ fontSize: font.xs, color: B.textMd, marginTop: 4 }}>
              {s.submittedBy || s.fromAddress || 'unknown sender'}
              {s.receivedUTC ? ` · ${fmt.date(s.receivedUTC)}` : ''}
              {` · ${s.docCount} document${s.docCount === 1 ? '' : 's'}`}
              {s.matchMethod && s.matchMethod !== 'manual' ? ` · matched by ${s.matchMethod}` : ''}
            </div>
            {data.maxPlanDocs && s.docCount > data.maxPlanDocs && (
              <div style={{ fontSize: font.xs, color: B.amber, marginTop: 4 }}>
                A review reads the first {data.maxPlanDocs} of these {s.docCount} documents; standards shown only on the rest will read as "Not shown".
              </div>
            )}
            {s.lastReviewStatus === 'error' && s.lastReviewMessage && (
              <div style={{ fontSize: font.xs, color: B.red, marginTop: 4 }}>{s.lastReviewMessage}</div>
            )}
            <div style={{ display: 'flex', gap: space.sm, marginTop: space.sm, flexWrap: 'wrap' }}>
              {s.lastReviewID && <Btn size="sm" variant="secondary" onClick={() => onOpenReport(s.lastReviewID)}>View report</Btn>}
              {canWrite && !isDup && (
                <Btn size="sm" onClick={() => runReview(s.submissionID)} loading={busy === s.submissionID || s.reviewRunning}
                     disabled={!s.docCount || s.reviewRunning}>
                  {s.lastReviewID ? 'Re-run review' : 'Run review'}
                </Btn>
              )}
              {canWrite && !s.reviewRunning && (
                <Btn size="sm" variant="secondary" loading={busy === `dup:${s.submissionID}`}
                     onClick={() => setDuplicate(!isDup)}>
                  {isDup ? 'Not a duplicate' : 'Mark as duplicate'}
                </Btn>
              )}
              {canWrite && !isDup && !s.docCount && s.source === 'email' && (
                <Btn size="sm" variant="secondary" loading={busy === `fetch:${s.submissionID}`}
                     onClick={async () => { setBusy(`fetch:${s.submissionID}`); await refetchAttachments(s.submissionID, toast); setBusy(null); reload(); }}>
                  Fetch attachments from email
                </Btn>
              )}
            </div>
          </div>
        );
      })}

      {documents.length > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: space.sm, margin: `${space.lg}px 0 ${space.sm}px`, flexWrap: 'wrap' }}>
            <div style={{ fontWeight: weight.semibold, color: B.text }}>Documents on file</div>
            {data.duplicateDocuments > 0 && (
              <>
                <Badge color={B.amber}>{data.duplicateDocuments} duplicate{data.duplicateDocuments === 1 ? '' : 's'}</Badge>
                {canWrite && (
                  <Btn size="sm" variant="secondary" loading={busy === 'dedupe'} onClick={async () => {
                    if (!window.confirm(`Remove ${data.duplicateDocuments} duplicate document${data.duplicateDocuments === 1 ? '' : 's'}? The oldest copy of each file is kept and every submission is pointed at it; the extra copies are deleted from the deal's Documents and SharePoint.`)) return;
                    setBusy('dedupe');
                    try {
                      const out = await api.post(`arc/lots/${lotId}/dedupe-documents`, {}, { timeout: 120000 });
                      toast.success(`Removed ${out.deleted} duplicate${out.deleted === 1 ? '' : 's'}.`
                        + (out.sharePointFailed?.length ? ` ${out.sharePointFailed.length} could not be removed from SharePoint.` : ''));
                    } catch (e) { toast.error(e.message || 'Could not remove the duplicates'); }
                    setBusy(null); reload();
                  }}>Remove duplicates</Btn>
                )}
              </>
            )}
          </div>
          <Table
            columns={[
              { key: 'fileName', label: 'File', render: (v, row) => (
                <button type="button" onClick={() => openArcDocument(row, toast)}
                        title="Open in a new tab"
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                                 color: B.navy, textDecoration: 'underline', font: 'inherit', textAlign: 'left' }}>
                  {v || 'Untitled'}
                </button>
              ) },
              { key: 'createdUTC', label: 'Received', render: v => fmt.date(v) },
              { key: 'fileSizeBytes', label: 'Size', align: 'right',
                render: v => (v == null ? '—' : `${(v / 1e6).toFixed(1)} MB`) },
            ]}
            data={documents.map(d => ({ ...d, id: d.documentID }))}
          />
        </>
      )}

      <ModalFooter><Btn variant="secondary" onClick={onClose}>Close</Btn></ModalFooter>
    </Modal>
  );
}

// Opens a lot document in a new tab. The SAS carries an inline disposition,
// so a PDF or photo renders in the browser; an Office file goes through
// Microsoft's read-only viewer. The tab is opened before the await so the
// popup blocker treats it as part of the click.
async function openArcDocument(doc, toast) {
  const win = window.open('', '_blank');
  try {
    const { sasUrl } = await api.get(`documents/${doc.documentID}/download-url`);
    const url = isOffice(doc.fileName) ? officeOnlineViewerUrl(sasUrl) : sasUrl;
    if (win) win.location.href = url; else window.open(url, '_blank');
  } catch (e) {
    if (win) win.close();
    toast.error(`Could not open ${doc.fileName || 'the document'}: ${e.message || e}`);
  }
}

// ── Standards & rules ──────────────────────────────────────────────────────
function StandardsTab({ community, canWrite, onChanged }) {
  const { toast } = useToast();
  const [extracting, setExtracting] = useState(false);
  const [openSet, setOpenSet] = useState(null);
  const [attaching, setAttaching] = useState(false);
  const { data, loading, error, reload } = useApi(
    () => api.get(`arc/communities/${community.communityID}`), [community.communityID]);

  // The extraction outlives its request: a 32k-token read takes minutes and the
  // edge answers a bodyless 500 at ~45 s while the server carries on. So the
  // POST is fired on a long budget and the COMMUNITY READ is the outcome — a
  // new rule-set row appears as 'extracting' before the model is called. The
  // request's own error is shown only if no such row ever appears.
  const extract = async () => {
    setExtracting(true);
    const priorVersion = Math.max(0, ...((data && data.ruleSets) || []).map(rs => rs.version || 0));
    let reqDone = false; let reqOut = null; let reqErr = null;
    api.post(`arc/communities/${community.communityID}/rules/extract`, {}, { timeout: 15 * 60 * 1000 })
      .then(out => { reqOut = out; }, e => { reqErr = e; })
      .finally(() => { reqDone = true; });

    const report = (rs) => {
      if (rs.status === 'error') toast.error(rs.message || 'Extraction failed');
      else toast.success(rs.message || `Read ${rs.ruleCount} requirements.`);
    };
    const started = Date.now();
    try {
      while (Date.now() - started < 40 * 60 * 1000) {
        await new Promise(r => setTimeout(r, 4000));
        // A refusal made before any row existed (no documents, no AI connection).
        if (reqDone && reqErr && reqErr.status >= 400 && reqErr.status < 500) {
          toast.error(reqErr.message || 'Extraction failed'); return;
        }
        if (reqDone && reqOut && (reqOut.error || reqOut.alreadyRunning) && !reqOut.ruleSetId) {
          toast.error(reqOut.error || reqOut.message); return;
        }
        let fresh;
        try { fresh = await api.get(`arc/communities/${community.communityID}`); } catch { continue; }
        // Snapshot: the request callback reassigns reqOut, so the find callback
        // must not close over the mutable binding (no-loop-func).
        const out = reqOut;
        const rs = (fresh.ruleSets || []).find(x => x.version > priorVersion)
          || (out && out.alreadyRunning && (fresh.ruleSets || []).find(x => x.ruleSetID === out.ruleSetId));
        if (rs && rs.status !== 'extracting') { report(rs); return; }
        // The request ended (edge 500, network cut) and the work never began.
        if (!rs && reqDone && reqErr && Date.now() - started > 60000) {
          toast.error(reqErr.message || 'Extraction failed'); return;
        }
      }
      toast.error('Still extracting — check back shortly.');
    } finally {
      setExtracting(false);
      reload(); onChanged && onChanged();
    }
  };

  const activate = async (ruleSetID) => {
    try {
      const out = await api.post(`arc/rulesets/${ruleSetID}/activate`, {});
      toast.success(`Standards v${out.version} adopted — ${out.ruleCount} requirements now in force.`);
      reload(); onChanged && onChanged();
    } catch (e) { toast.error(e.message || 'Could not activate'); }
  };

  if (loading) return <Spinner />;
  if (error) return <ErrorMsg msg={String(error)} />;

  const { standards, ruleSets } = data;
  const readable = standards.filter(s => s.hasText).length;

  return (
    <>
      <Card style={{ marginBottom: space.lg }}>
        <div style={{ fontWeight: weight.semibold, marginBottom: space.sm }}>Governing documents</div>
        {standards.length === 0 && (
          <Empty>
            No governing documents yet. Use + Add a document to upload the design code and
            covenants — they are stored on the community&rsquo;s deal (set under Edit).
          </Empty>
        )}
        {standards.map(s => (
          <div key={s.standardID} style={{ display: 'flex', gap: space.sm, alignItems: 'center', padding: '6px 0' }}>
            <span style={{ color: B.text }}>{s.title}</span>
            {s.kind && <Badge color={B.textMd}>{s.kind}</Badge>}
            {/* A document still going through OCR has no text to extract rules
                from, and saying so beats an extraction that quietly finds none. */}
            {!s.hasText && <Badge color="#8a6100">not read yet</Badge>}
          </div>
        ))}
        {canWrite && (
          <div style={{ marginTop: space.md, display: 'flex', gap: space.sm, flexWrap: 'wrap', alignItems: 'center' }}>
            <Btn size="sm" variant="secondary" onClick={() => setAttaching(true)}>+ Add a document</Btn>
            {standards.length > 0 && (
              <Btn onClick={extract} loading={extracting} disabled={!readable}>
                {ruleSets.length ? 'Re-read the documents' : 'Extract the standards'}
              </Btn>
            )}
          </div>
        )}
        {canWrite && standards.length > 0 && (
          <div style={{ fontSize: font.xs, color: B.textMd, marginTop: 6 }}>
            Produces a <strong>draft</strong> set of requirements for the committee to review.
            Nothing is measured against it until it is adopted.
          </div>
        )}
      </Card>

      <div style={{ fontWeight: weight.semibold, marginBottom: space.sm }}>Rule sets</div>
      {ruleSets.length === 0 && <Empty>No rule set has been extracted yet.</Empty>}
      {ruleSets.map(rs => (
        <Card key={rs.ruleSetID} style={{ marginBottom: space.sm }}>
          <div style={{ display: 'flex', gap: space.sm, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: weight.semibold, color: B.text }}>Version {rs.version}</span>
            <Badge color={rs.status === 'active' ? '#1c6b40' : rs.status === 'error' ? B.red : B.textMd}>
              {rs.status === 'active' ? 'In force' : rs.status === 'draft' ? 'Draft — not in force' : rs.status === 'extracting' ? 'Extracting…' : rs.status}
            </Badge>
            <span style={{ color: B.textMd, fontSize: font.sm }}>{rs.ruleCount} requirements</span>
            {rs.activatedUTC && <span style={{ color: B.textLt, fontSize: font.xs }}>adopted {fmt.date(rs.activatedUTC)} by {rs.activatedBy}</span>}
          </div>
          {rs.message && <div style={{ fontSize: font.sm, color: rs.status === 'error' ? B.red : B.textMd, marginTop: 6 }}>{rs.message}</div>}
          <div style={{ display: 'flex', gap: space.sm, marginTop: space.sm }}>
            <Btn size="sm" variant="secondary" onClick={() => setOpenSet(rs.ruleSetID)}>View requirements</Btn>
            {canWrite && rs.status === 'draft' && rs.ruleCount > 0 && (
              <Btn size="sm" onClick={() => activate(rs.ruleSetID)}>Adopt as the standards</Btn>
            )}
          </div>
        </Card>
      ))}

      {openSet && <RuleSetModal ruleSetId={openSet} onClose={() => setOpenSet(null)} />}
      {attaching && (
        <AttachStandardModal community={community} toast={toast}
          onClose={() => setAttaching(false)}
          onAttached={() => { setAttaching(false); reload(); onChanged && onChanged(); }} />
      )}
    </>
  );
}

// Governing documents live on dbo.Documents like any other file — on the
// community's deal. This dialog does the whole job in one place: link a deal if
// the community has none, UPLOAD the design code / covenants straight onto that
// deal (the ordinary upload-url → PUT → confirm-upload path, so they go through
// OCR like every other document), or pick one already there — then point an
// ArcStandards row at each. It used to only pick an existing document, and to
// dead-end on "link a deal via the API", which left no way to add one here.
const STANDARD_KIND_OPTIONS = [
  { value: '', label: '— not set —' },
  { value: 'covenants', label: 'Covenants' },
  { value: 'guidelines', label: 'Design guidelines' },
  { value: 'plat', label: 'Plat' },
];

async function uploadToDeal(file, dealId, dealDetailId) {
  const ext = file.name.includes('.') ? file.name.split('.').pop().toLowerCase() : undefined;
  const up = await api.post('document-upload-url', {
    fileName: file.name, fileType: ext, dealId: dealId || null, dealDetailId: dealDetailId || null,
    description: 'ARC governing document',
  });
  if (!up || !up.sasUrl) throw new Error('No upload URL was returned — is storage configured?');
  const res = await fetch(up.sasUrl, {
    method: 'PUT',
    headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!res.ok) throw new Error(`Uploading ${file.name} failed (${res.status})`);
  const conf = await api.post(`documents/${up.documentId}/confirm-upload`,
    { fileSizeBytes: file.size, versionId: up.versionId }, { timeout: 60000 });
  // confirm-upload discards a torn transfer rather than keeping bad bytes.
  if (conf && conf.discarded) throw new Error(`${file.name} did not upload completely — try it again`);
  return up.documentId;
}

function AttachStandardModal({ community, onClose, onAttached, toast }) {
  const [linkedDealId, setLinkedDealId] = useState(community.dealID || '');
  const dealId = linkedDealId;
  const dealDetailId = linkedDealId === community.dealID ? (community.dealDetailID || null) : null;

  const { dealPickerItems } = useDealPickerItems();
  const dealOnlyItems = useMemo(() => dealPickerItems.filter(i => !i.dealDetailID), [dealPickerItems]);
  const [pickDeal, setPickDeal] = useState('');
  const [linking, setLinking] = useState(false);

  const { data, loading, error } = useApi(
    () => (dealId ? api.get(`documents?dealId=${dealId}`) : Promise.resolve([])),
    [dealId]);
  const [mode, setMode] = useState('upload');
  const [files, setFiles] = useState([]);
  const [documentId, setDocumentId] = useState('');
  const [kind, setKind] = useState('guidelines');
  const [saving, setSaving] = useState(false);

  const docList = Array.isArray(data) ? data : ((data && data.documents) || []);
  const docItems = docList.map(d => ({
    value: d.documentID,
    label: d.fileName,
    subtitle: [d.folderPath, d.createdUTC ? fmt.date(d.createdUTC) : null].filter(Boolean).join(' · ') || null,
  }));

  const linkDeal = async () => {
    const item = dealOnlyItems.find(i => i.value === pickDeal);
    if (!item) return;
    setLinking(true);
    try {
      await api.put(`arc/communities/${community.communityID}`, { dealId: item.dealID });
      setLinkedDealId(item.dealID);
      toast.success(`${community.name} is now linked to ${item.label}.`);
    } catch (e) { toast.error(e.message || 'Could not link the deal'); }
    finally { setLinking(false); }
  };

  const attach = (docId, title) => api.post(`arc/communities/${community.communityID}/standards`,
    { documentId: docId, kind: kind || null, ...(title ? { title } : {}) });

  const save = async () => {
    setSaving(true);
    try {
      if (mode === 'upload') {
        let done = 0;
        const failed = [];
        for (const f of files) {
          try {
            const docId = await uploadToDeal(f, dealId, dealDetailId);
            await attach(docId, f.name);
            done++;
          } catch (e) { failed.push(`${f.name}: ${e.message}`); }
        }
        if (failed.length) toast.error(`${failed.length} file${failed.length === 1 ? '' : 's'} failed — ${failed[0]}`);
        if (done) {
          toast.success(`Uploaded and attached ${done} document${done === 1 ? '' : 's'}. `
            + 'They show “not read yet” until OCR finishes, usually a minute or two.');
          onAttached();
        }
      } else {
        await attach(documentId);
        toast.success('Attached.');
        onAttached();
      }
    } catch (e) { toast.error(e.message || 'Could not attach the document'); }
    finally { setSaving(false); }
  };

  const canSave = mode === 'upload' ? files.length > 0 : !!documentId;

  return (
    <Modal title="Add a governing document" onClose={onClose} width={560}>
      {!dealId ? (
        <>
          <div style={{ fontSize: font.sm, color: B.textMd, marginBottom: space.sm }}>
            A community&rsquo;s documents are stored on its deal, and this community isn&rsquo;t
            linked to one yet. Choose the deal it belongs to.
          </div>
          <InlineSearchPicker label="Deal" items={dealOnlyItems} value={pickDeal}
            onChange={setPickDeal} placeholder="Search deals…" />
          <Btn size="sm" onClick={linkDeal} loading={linking} disabled={!pickDeal}>Link this deal</Btn>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', gap: space.sm, marginBottom: space.md }}>
            <Btn size="sm" variant={mode === 'upload' ? 'primary' : 'secondary'} onClick={() => setMode('upload')}>Upload a file</Btn>
            <Btn size="sm" variant={mode === 'existing' ? 'primary' : 'secondary'} onClick={() => setMode('existing')}>Pick one already in Documents</Btn>
          </div>
          {mode === 'upload' ? (
            <div style={{ marginBottom: space.md }}>
              <input type="file" multiple accept=".pdf,.doc,.docx,.txt,.rtf,image/*"
                     onChange={e => setFiles(Array.from(e.target.files || []))} />
              {files.length > 0 && (
                <div style={{ fontSize: font.xs, color: B.textMd, marginTop: 6 }}>
                  {files.map(f => f.name).join(', ')}
                </div>
              )}
              <div style={{ fontSize: font.xs, color: B.textMd, marginTop: 6 }}>
                Saved to the deal&rsquo;s Documents and attached here as a governing document.
              </div>
            </div>
          ) : loading ? <Spinner /> : error ? <ErrorMsg msg={String(error)} /> : docItems.length === 0 ? (
            <Empty>No documents are on this deal yet — use Upload a file instead.</Empty>
          ) : (
            <InlineSearchPicker label="Document" items={docItems} value={documentId}
              onChange={setDocumentId} placeholder="Search the deal's documents…" />
          )}
          <Select label="Kind" value={kind} onChange={setKind} options={STANDARD_KIND_OPTIONS} />
        </>
      )}
      <ModalFooter>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        {dealId && (
          <Btn onClick={save} loading={saving} disabled={!canSave}>
            {mode === 'upload' ? 'Upload and attach' : 'Attach'}
          </Btn>
        )}
      </ModalFooter>
    </Modal>
  );
}

function RuleSetModal({ ruleSetId, onClose }) {
  const { data, loading, error } = useApi(() => api.get(`arc/rulesets/${ruleSetId}`), [ruleSetId]);
  if (loading) return <Modal title="Requirements" onClose={onClose} width={820}><Spinner /></Modal>;
  if (error || !data) return <Modal title="Requirements" onClose={onClose} width={820}><ErrorMsg msg={String(error || 'not found')} /></Modal>;
  return (
    <Modal title={`Requirements — v${data.ruleSet.version}`} onClose={onClose} width={820}>
      {data.rules.map(r => (
        <div key={r.ruleId} style={{ borderBottom: `1px solid ${B.divider}`, padding: `${space.sm}px 0` }}>
          <div style={{ display: 'flex', gap: space.sm, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: font.xs, color: B.textLt }}>{r.code}</span>
            <span style={{ fontWeight: weight.semibold, color: B.text }}>{r.title}</span>
            {r.category && <Badge color={B.navy}>{r.category}</Badge>}
            {r.severity !== 'required' && <Badge color={B.textMd}>{r.severity}</Badge>}
          </div>
          <div style={{ fontSize: font.sm, color: B.textMd, marginTop: 4 }}>{r.requirement}</div>
          {r.sourceQuote && (
            <div style={{ fontSize: font.xs, color: B.textMd, marginTop: 6, paddingLeft: space.sm,
                          borderLeft: `2px solid ${B.divider}`, fontStyle: 'italic' }}>
              &ldquo;{r.sourceQuote}&rdquo;{r.sourceRef ? ` — ${r.sourceRef}` : ''}
            </div>
          )}
        </div>
      ))}
      <ModalFooter><Btn variant="secondary" onClick={onClose}>Close</Btn></ModalFooter>
    </Modal>
  );
}

// Says, in words, the three reasons a report may not be there yet — and lets an
// Admin turn the whole thing off for this community. A committee told "reviews
// are automatic" and shown nothing has no way to tell a quiet week from a
// broken feature.
function AutoReviewBar({ community, canWrite, onChanged }) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const on = !!community.autoReview;
  const ready = community.activeRuleSetVersion != null;

  const toggle = async () => {
    setSaving(true);
    try {
      await api.put(`arc/communities/${community.communityID}`, { autoReview: !on });
      toast.success(!on
        ? 'Plans will be reviewed as they arrive.'
        : 'Automatic review is off — submissions wait for Run review.');
      onChanged && onChanged();
    } catch (e) { toast.error(e.message || 'Could not change it'); }
    finally { setSaving(false); }
  };

  return (
    <Card style={{ marginBottom: space.lg, display: 'flex', gap: space.md, alignItems: 'center', flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 260 }}>
        <div style={{ fontWeight: weight.semibold, color: B.text }}>
          {on ? 'Plans are reviewed as they arrive' : 'Plans are reviewed when you ask'}
        </div>
        <div style={{ fontSize: font.sm, color: B.textMd, marginTop: 4 }}>
          {on ? (
            ready
              ? <>A submission is left for a few minutes first, in case the rest of the packet is
                  still coming, then measured against the adopted standards. One that arrived without
                  a lot number waits in the Inbox until somebody files it — a report belongs in a
                  lot&rsquo;s file, so it is never produced on a guess. Every report is still a draft
                  until a committee member releases it.</>
              : <>Nothing is reviewed until this community has adopted a set of standards
                  &mdash; see the Standards tab.</>
          ) : (
            <>Submissions land and wait. Open a lot and press <strong>Run review</strong>.</>
          )}
        </div>
      </div>
      {canWrite && (
        <Btn size="sm" variant={on ? 'secondary' : 'primary'} onClick={toggle} loading={saving}>
          {on ? 'Turn off' : 'Review on arrival'}
        </Btn>
      )}
    </Card>
  );
}

// Capture depends on a live Graph webhook subscription (renewed every 6h) or
// somebody pressing Scan now — there is otherwise no timer that polls the
// mailbox on its own, unlike Sage Import / Email Inbox. This is the one place
// to see why a forwarded plan hasn't shown up yet, and to force it in now
// rather than wait on the webhook.
function EmailIntakePanel({ canWrite }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [nonce, setNonce] = useState(0);
  const { data, loading, error } = useApi(
    () => (canWrite ? api.get('arc/email/diagnostics') : Promise.resolve(null)), [canWrite, nonce]);

  if (!canWrite) return null;

  const scan = async () => {
    setScanning(true);
    try {
      const res = await api.post('arc/email/scan', {});
      const captured = (res.results || []).filter(r => r.status === 'captured').length;
      const recovered = (res.results || []).filter(r => r.status === 'attachments-recovered')
        .reduce((n, r) => n + (r.documentCount || 0), 0);
      toast.success(res.scanned
        ? `Checked ${res.scanned} message${res.scanned === 1 ? '' : 's'} — ${captured} captured`
          + (recovered ? `, ${recovered} missing file${recovered === 1 ? '' : 's'} recovered.` : '.')
        : 'Checked the mailbox — nothing new.');
      setNonce(n => n + 1);
    } catch (e) { toast.error(e.message || 'Could not scan the mailbox'); }
    finally { setScanning(false); }
  };

  const recover = async () => {
    setRecovering(true);
    try {
      const res = await api.post('arc/email/recover-attachments', {}, { timeout: 60000 });
      const failed = (res.results || []).filter(r => r.ok === false);
      if (!res.checked) toast.success('Every emailed submission already has its files.');
      else toast.success(`Checked ${res.checked} submission${res.checked === 1 ? '' : 's'} with no files — `
        + `${res.recovered} file${res.recovered === 1 ? '' : 's'} recovered on ${res.submissionsFixed}.`
        + (failed.length ? ` ${failed.length} could not be fetched (${failed[0].reason}).` : '')
        + (res.remaining ? ' More remain — press it again.' : ''));
      setNonce(n => n + 1);
    } catch (e) { toast.error(e.message || 'Could not recover the attachments'); }
    finally { setRecovering(false); }
  };

  return (
    <Card style={{ marginBottom: space.lg }}>
      <div style={{ display: 'flex', gap: space.md, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ fontWeight: weight.semibold, color: B.text }}>Email intake</div>
          <div style={{ fontSize: font.sm, color: B.textMd, marginTop: 4 }}>
            A forwarded plan lands here on its own once a Graph subscription is live — a scan doesn't wait for that.
          </div>
        </div>
        <Btn size="sm" variant="secondary" onClick={scan} loading={scanning}>
          {scanning ? 'Scanning…' : 'Scan mailbox now'}
        </Btn>
        <Btn size="sm" variant="secondary" onClick={recover} loading={recovering}
             title="Re-read every emailed submission that has no files and land its attachments">
          Recover missing attachments
        </Btn>
        <Btn size="sm" variant="secondary" onClick={() => setOpen(o => !o)}>
          {open ? 'Hide details' : 'Show details'}
        </Btn>
      </div>
      {open && (
        <div style={{ marginTop: space.md, borderTop: `1px solid ${B.border}`, paddingTop: space.md }}>
          {loading ? <Spinner /> : error ? <ErrorMsg msg={String(error)} /> : data && (
            <>
              <div style={{ fontSize: font.sm, color: B.textMd, marginBottom: space.sm }}>
                Mailbox: <code>{data.mailbox || '(not set)'}</code>
                {data.storedSubscriptionId && data.storedSubscriptionExpiry && (
                  <> · subscription renews until {fmt.date(data.storedSubscriptionExpiry)}</>
                )}
              </div>
              {data.communities.length > 0 && (
                <div style={{ fontSize: font.sm, color: B.textMd, marginBottom: space.sm }}>
                  {data.communities.map(c => (
                    <div key={c.name}>{c.name}: <code>{c.address || '(no intake tag)'}</code></div>
                  ))}
                </div>
              )}
              {data.hints.map((h, i) => (
                <div key={i} style={{ fontSize: font.sm, color: '#8a6100', marginTop: 4 }}>⚠ {h}</div>
              ))}
              {data.hints.length === 0 && (
                <div style={{ fontSize: font.sm, color: '#1c6b40' }}>Configured — a scan should capture anything waiting.</div>
              )}
            </>
          )}
        </div>
      )}
    </Card>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────
export function ArcReview({ user }) {
  const { toast } = useToast();
  const canWrite = !!(user && (user.Role === 'Admin' || user.role === 'Admin'));
  const [tab, setTab] = useState('Lots');
  const [selected, setSelected] = useState(null);
  const [openLot, setOpenLot] = useState(null);
  const [openReport, setOpenReport] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [nonce, setNonce] = useState(0);

  const { data, loading, error, reload } = useApi(() => api.get('arc/communities'), [nonce]);
  const communities = (data && data.communities) || [];
  const community = useMemo(
    () => communities.find(c => c.communityID === selected) || communities[0] || null,
    [communities, selected]);

  const bump = () => setNonce(n => n + 1);

  if (loading) return <Spinner />;
  if (error) return <ErrorMsg msg={String(error)} />;
  if (data && data.available === false) {
    return <Card><Empty>{data.error || 'ARC review is not set up on this database yet.'}</Empty></Card>;
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: space.md, alignItems: 'center', flexWrap: 'wrap', marginBottom: space.lg }}>
        <h1 style={{ margin: 0, fontSize: font.xxl, color: B.navy }}>Architectural Review</h1>
        {communities.length > 1 && (
          <Select value={community ? community.communityID : ''} onChange={v => setSelected(v)}
                  options={communities.map(c => ({ value: c.communityID, label: c.name }))} />
        )}
        {community && (
          <>
            {/* The mailbox a community is reached at is otherwise invisible
                once the New Community modal that set it has closed — this is
                the one place it's shown again, for whichever community is on
                screen. */}
            <span style={{ fontSize: font.xs, color: B.textMd }}>
              import+{community.intakeTag || 'arc'}@harvus.com
            </span>
            <span style={{ fontSize: font.xs, color: community.dealID ? B.textMd : '#8a6100' }}>
              {community.dealID ? `Deal: ${community.dealName || 'linked'}` : 'No deal linked'}
            </span>
            {canWrite && <Btn size="sm" variant="secondary" onClick={() => setEditing(true)}>Edit</Btn>}
          </>
        )}
        {canWrite && <Btn size="sm" variant="secondary" onClick={() => setAdding(true)}>New community</Btn>}
      </div>

      {communities.length === 0 && (
        <Card><Empty>
          No community is set up yet. Create one, give it the intake tag <code>arc</code>,
          then mail to <code>import+arc@harvus.com</code> lands here.
        </Empty></Card>
      )}

      {community && (
        <>
          <div style={{ display: 'flex', gap: space.md, flexWrap: 'wrap', marginBottom: space.lg }}>
            <KpiCard label="Lots" value={community.lotCount} />
            <KpiCard label="Awaiting a lot" value={community.unassignedCount}
                     color={community.unassignedCount ? '#8a6100' : B.textMd}
                     sub={community.unassignedCount ? 'submissions nobody has placed' : null} />
            <KpiCard label="Standards"
                     value={community.activeRuleSetVersion == null ? '—' : `v${community.activeRuleSetVersion}`}
                     sub={community.activeRuleSetVersion == null ? 'none adopted yet' : 'in force'}
                     color={community.activeRuleSetVersion == null ? '#8a6100' : B.navy} />
            {data.supportsAutoReview && (
              <KpiCard label="On arrival"
                       value={community.autoReview ? 'Reviewed' : 'Manual'}
                       sub={community.autoReview
                         ? (community.activeRuleSetVersion == null
                             ? 'waiting on adopted standards'
                             : 'plans are read as they land')
                         : 'somebody presses Run review'}
                       color={community.autoReview && community.activeRuleSetVersion != null ? '#1c6b40' : B.textMd} />
            )}
          </div>

          <EmailIntakePanel canWrite={canWrite} />

          {data.supportsAutoReview && (
            <AutoReviewBar community={community} canWrite={canWrite} onChanged={bump} />
          )}

          <Tabs tabs={['Lots', 'Inbox', 'Standards', 'Colours']} active={tab} onChange={setTab} />

          {tab === 'Lots'      && <LotsTab community={community} canWrite={canWrite} onOpen={setOpenLot} onChanged={bump} />}
          {tab === 'Inbox'     && <InboxTab community={community} canWrite={canWrite} onChanged={bump} />}
          {tab === 'Standards' && <StandardsTab community={community} canWrite={canWrite} onChanged={bump} />}
          {tab === 'Colours'   && <MaterialColoursTab canWrite={canWrite} />}
        </>
      )}

      {openLot && (
        <LotFileModal lotId={openLot} canWrite={canWrite}
                      onClose={() => { setOpenLot(null); bump(); }}
                      onOpenReport={(rid) => setOpenReport(rid)} />
      )}
      {openReport && (
        <ReportModal reviewId={openReport} canWrite={canWrite}
                     onClose={() => setOpenReport(null)} onChanged={bump} />
      )}
      {adding && (
        <NewCommunityModal
          onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); reload(); }}
          toast={toast}
          existingTags={communities.filter(c => c.intakeTag).map(c => ({ intakeTag: c.intakeTag, name: c.name }))}
        />
      )}
      {editing && community && (
        <EditCommunityModal
          community={community}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); reload(); }}
          toast={toast}
          existingTags={communities
            .filter(c => c.intakeTag && c.communityID !== community.communityID)
            .map(c => ({ intakeTag: c.intakeTag, name: c.name }))}
        />
      )}
    </div>
  );
}

// ── Looked-up material colours (shared by every community) ─────────────────
// A colour found on the web is reused by every later rendering, so a wrong one
// repeats until somebody fixes it here. Correcting or deleting changes FUTURE
// renderings only; a picture already drawn keeps its colours until redrawn.
const COLOUR_TIER = {
  manufacturer: { label: 'Manufacturer page', color: '#1c6b40' },
  reference:    { label: 'Reference site — approximate', color: '#8a6100' },
  manual:       { label: 'Entered by hand', color: '#1c6b40' },
};
function MaterialColoursTab({ canWrite }) {
  const { toast } = useToast();
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  const [status, setStatus] = useState('');
  const [nonce, setNonce] = useState(0);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(null);
  useEffect(() => { const t = setTimeout(() => setTerm(q.trim()), 300); return () => clearTimeout(t); }, [q]);
  const { data, loading, error } = useApi(
    () => api.get(`arc/material-colours?q=${encodeURIComponent(term)}&status=${encodeURIComponent(status)}`), [term, status, nonce]);
  const items = (data && data.items) || [];
  const reload = () => setNonce(n => n + 1);

  const remove = async (m) => {
    const what = [m.manufacturer, m.product, m.colour].filter(Boolean).join(' ');
    if (!window.confirm(`Delete the saved colour for ${what}? The next rendering that names it searches again.`)) return;
    setBusy(m.materialColorID);
    try { await api.del(`arc/material-colours/${m.materialColorID}`); toast.success('Deleted — it will be searched again next time.'); reload(); }
    catch (e) { toast.error(String(e.message || e)); }
    finally { setBusy(null); }
  };
  const research = async (m) => {
    setBusy(m.materialColorID);
    try {
      const res = await api.post(`arc/material-colours/${m.materialColorID}/lookup`, {}, { timeout: 60000 });
      (res.saved ? toast.success : toast.error)(res.message);
    } catch (e) {
      toast.error('The search did not answer in time. It may still save a result — refresh in a minute.');
    } finally { setBusy(null); reload(); }
  };

  if (data && data.available === false) return <Card><Empty>{data.error}</Empty></Card>;

  return (
    <Card>
      <div style={{ fontSize: font.sm, color: B.textMd, marginBottom: space.sm }}>
        Colours looked up for renderings, shared by every community. A found colour is reused until it is
        changed or deleted here; a colour that was not found is searched again after {(data && data.retryDays) || 30} days.
        Changes apply to renderings drawn from now on.
      </div>
      <div style={{ display: 'flex', gap: space.sm, flexWrap: 'wrap', marginBottom: space.sm }}>
        <div style={{ flex: '1 1 240px' }}>
          <Input value={q} onChange={setQ} placeholder="Search manufacturer, product or colour…" />
        </div>
        <Select value={status} onChange={v => setStatus(v)} options={[
          { value: '', label: 'All' }, { value: 'found', label: 'Found' },
          { value: 'not-found', label: 'Not found' }, { value: 'manual', label: 'Entered by hand' },
        ]} />
      </div>
      {loading && <Spinner />}
      {error && <ErrorMsg msg={String(error)} />}
      {!loading && !error && items.length === 0 && <Empty>No colours saved yet — they appear here after a rendering looks one up.</Empty>}
      {items.map(m => {
        const tier = COLOUR_TIER[m.tier];
        return (
          <div key={m.materialColorID} style={{ display: 'flex', gap: space.sm, alignItems: 'flex-start', padding: '8px 0', borderBottom: `1px solid ${B.divider}` }}>
            <div style={{ width: 28, paddingTop: 2 }}>{m.hex ? <Swatch hex={m.hex} /> : null}</div>
            <div style={{ flex: 1, minWidth: 0, fontSize: font.sm }}>
              <div style={{ color: B.text, fontWeight: weight.semibold }}>
                {[m.manufacturer, m.product, m.colour].filter(Boolean).join(' · ')}
              </div>
              {m.status === 'found' ? (
                <div style={{ fontSize: font.xs, color: B.textMd }}>
                  {tier && <span style={{ color: tier.color, fontWeight: weight.semibold }}>{m.tier === 'manual' ? m.label : tier.label}</span>}
                  {m.name && <span> · {m.name}</span>}
                  {m.hex ? <span> · {m.hex}</span> : <span> · no colour value, description only</span>}
                  {m.description && <div>{m.description}</div>}
                  {m.url && <div><a href={m.url} target="_blank" rel="noreferrer" style={{ color: B.navy }}>Source</a></div>}
                </div>
              ) : (
                <div style={{ fontSize: font.xs, color: '#8a6100' }}>
                  Not found{m.reason ? ` — ${m.reason}` : ''}. Searched again after {fmt.date(m.retryAfterUTC)}.
                </div>
              )}
              <div style={{ fontSize: font.xs, color: B.textLt }}>Updated {fmt.date(m.modifiedUTC)}{m.modifiedBy ? ` by ${m.modifiedBy}` : ''}</div>
            </div>
            {canWrite && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <Btn size="sm" variant="secondary" disabled={busy === m.materialColorID} onClick={() => setEditing(m)}>Correct</Btn>
                <Btn size="sm" variant="secondary" disabled={busy === m.materialColorID} onClick={() => research(m)}>
                  {busy === m.materialColorID ? 'Working…' : 'Search again'}
                </Btn>
                <Btn size="sm" variant="secondary" disabled={busy === m.materialColorID} onClick={() => remove(m)}>Delete</Btn>
              </div>
            )}
          </div>
        );
      })}
      {data && data.truncated && <div style={{ fontSize: font.xs, color: B.textMd, marginTop: space.sm }}>Showing the 300 most recent — search to narrow.</div>}
      {editing && <CorrectColourModal m={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </Card>
  );
}

function CorrectColourModal({ m, onClose, onSaved }) {
  const { toast } = useToast();
  const [hex, setHex] = useState(m.hex || '');
  const [name, setName] = useState(m.name || '');
  const [description, setDescription] = useState(m.description || '');
  const [url, setUrl] = useState(m.url || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const shown = /^#?[0-9a-f]{6}$/i.test(hex.trim()) ? `#${hex.trim().replace(/^#/, '')}` : null;
  const save = async () => {
    setSaving(true); setErr('');
    try {
      await api.put(`arc/material-colours/${m.materialColorID}`, { hex: hex.trim(), name, description, url: url.trim() });
      toast.success('Saved — renderings drawn from now on use it.');
      onSaved();
    } catch (e) { setErr(String(e.message || e)); }
    finally { setSaving(false); }
  };
  return (
    <Modal title={`Correct: ${[m.manufacturer, m.product, m.colour].filter(Boolean).join(' ')}`} onClose={onClose}>
      <div style={{ fontSize: font.sm, color: B.textMd, marginBottom: space.sm }}>
        Give the colour value, a description of how it looks, or both (shingles, brick and stone are blends a single value does not describe).
      </div>
      <div style={{ display: 'flex', gap: space.sm, alignItems: 'center', marginBottom: space.sm }}>
        <div style={{ flex: 1 }}><Input value={hex} onChange={setHex} placeholder="#434341" /></div>
        {shown && <Swatch hex={shown} />}
      </div>
      <div style={{ marginBottom: space.sm }}><Input value={name} onChange={setName} placeholder="Colour name (optional)" /></div>
      <div style={{ marginBottom: space.sm }}><Textarea value={description} onChange={v => setDescription(v)} placeholder="How it looks, e.g. charcoal grey with black and dark brown granules" rows={3} /></div>
      <div style={{ marginBottom: space.sm }}><Input value={url} onChange={setUrl} placeholder="Source link, https://… (optional)" /></div>
      {err && <ErrorMsg msg={err} />}
      <ModalFooter>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        <Btn onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Btn>
      </ModalFooter>
    </Modal>
  );
}

// Submission filter on the Lots tab. A lot with submissions but no review yet
// is its own state ("not reviewed"), never folded into "no submissions".
const LOT_SUBMISSION_FILTERS = [
  { value: '', label: 'All lots' },
  { value: 'with', label: 'Has submissions' },
  { value: 'none', label: 'No submissions' },
  { value: 'unreviewed', label: 'Submitted, not reviewed' },
  { value: 'non-compliant', label: 'Latest: not compliant' },
  { value: 'needs-info', label: 'Latest: more information needed' },
  { value: 'compliant', label: 'Latest: appears compliant' },
];
function lotMatchesSubmissionFilter(lot, filter) {
  const count = Number(lot.submissionCount) || 0;
  switch (filter) {
    case 'with': return count > 0;
    case 'none': return count === 0;
    case 'unreviewed': return count > 0 && !lot.lastVerdict;
    case 'non-compliant': case 'needs-info': case 'compliant': return lot.lastVerdict === filter;
    default: return true;
  }
}

function LotsTab({ community, canWrite, onOpen, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [q, setQ] = useState('');
  const [subFilter, setSubFilter] = useState('');
  const { data, loading, error, reload } = useApi(
    () => api.get(`arc/communities/${community.communityID}/lots`), [community.communityID]);

  if (loading) return <Spinner />;
  if (error) return <ErrorMsg msg={String(error)} />;
  const lots = (data && data.lots) || [];
  const term = q.trim().toLowerCase();
  const shown = lots
    .filter(l => lotMatchesSubmissionFilter(l, subFilter))
    .filter(l => !term || [l.lotCode, l.address, l.ownerName, l.ownerEmail, l.builderName]
      .some(v => String(v || '').toLowerCase().includes(term)));

  return (
    <>
      <div style={{ display: 'flex', gap: space.sm, marginBottom: space.md, flexWrap: 'wrap' }}>
        <Input value={q} onChange={setQ} placeholder="Search lot, address, owner or builder" />
        <Select value={subFilter} onChange={v => setSubFilter(v)} options={LOT_SUBMISSION_FILTERS} />
        {canWrite && <Btn size="sm" onClick={() => setAdding(true)}>Add lot</Btn>}
        {canWrite && <Btn size="sm" variant="secondary" onClick={() => setImporting(true)}>Import lots (CSV / Excel)</Btn>}
      </div>
      <Table
        columns={[
          { key: 'lotCode', label: 'Lot' },
          { key: 'address', label: 'Address' },
          { key: 'ownerName', label: 'Owner' },
          { key: 'submissionCount', label: 'Submissions', align: 'right' },
          { key: 'lastVerdict', label: 'Latest review',
            render: (v, row) => {
              if (!v) return row.submissionCount ? <span style={{ color: B.textMd }}>not reviewed</span> : '—';
              const s = VERDICT_STYLE[v] || { label: v, color: B.textMd };
              return <span style={{ color: s.color, fontWeight: weight.semibold }}>{s.label}</span>;
            } },
          { key: 'lastReviewedUTC', label: 'Reviewed', render: v => (v ? fmt.date(v) : '—') },
        ]}
        data={shown.map(l => ({ ...l, id: l.lotID }))}
        onRowClick={(row) => onOpen(row.lotID)}
      />
      {(subFilter || term) && (
        <div style={{ fontSize: font.sm, color: B.textMd, marginTop: space.sm }}>
          Showing {shown.length} of {lots.length} lots.
        </div>
      )}
      {adding && (
        <LotModal communityId={community.communityID}
                  onClose={() => setAdding(false)}
                  onSaved={() => { setAdding(false); reload(); onChanged && onChanged(); }} />
      )}
      {importing && (
        <ImportLotsModal communityId={community.communityID}
                         onClose={() => setImporting(false)}
                         onImported={() => { reload(); onChanged && onChanged(); }} />
      )}
    </>
  );
}

// Lot roster import. The server parses the file (one parser for every route
// in) and answers a PREVIEW first — new lots, lots whose details change, and
// every row it could not use — so nothing is written until somebody has seen
// what the sheet will do to the files on this community. An empty cell never
// clears a field, and a lot missing from the sheet is left alone.
const LOT_FIELD_LABELS = {
  phase: 'Phase', address: 'Address', ownerName: 'Owner', ownerEmail: 'Owner email',
  builderName: 'Builder', builderEmail: 'Builder email', acres: 'Acres', notes: 'Notes', lotCode: 'Lot',
};
function readFileBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || '').replace(/^data:[^,]*,/, ''));
    r.onerror = () => reject(new Error('Could not read the file'));
    r.readAsDataURL(file);
  });
}
function ImportLotsModal({ communityId, onClose, onImported }) {
  const { toast } = useToast();
  const [file, setFile] = useState(null);
  const [payload, setPayload] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const pick = async (f) => {
    setFile(f); setPreview(null); setResult(null); setErr(null); setPayload(null);
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { setErr('That file is larger than 5 MB — a lot roster should be far smaller.'); return; }
    setBusy(true);
    try {
      const body = { fileName: f.name, contentBase64: await readFileBase64(f) };
      setPayload(body);
      setPreview(await api.post(`arc/communities/${communityId}/lots/import`, { ...body, dryRun: true }));
    } catch (e) { setErr(e.message || 'Could not read the file'); }
    finally { setBusy(false); }
  };

  const run = async () => {
    if (!payload) return;
    setBusy(true); setErr(null);
    try {
      const res = await api.post(`arc/communities/${communityId}/lots/import`, payload);
      setResult(res);
      const failed = (res.failures || []).length;
      if (failed) toast.error(`${res.created} added, ${res.updated} updated — ${failed} could not be written.`);
      else toast.success(`${res.created} lot${res.created === 1 ? '' : 's'} added, ${res.updated} updated.`);
      onImported && onImported();
    } catch (e) { setErr(e.message || 'Import failed'); }
    finally { setBusy(false); }
  };

  const shown = result || preview;
  const nothingToDo = preview && !preview.toCreate && !preview.toUpdate;

  return (
    <Modal title="Import lots" onClose={onClose} width={760}>
      <div style={{ fontSize: font.sm, color: B.textMd, marginBottom: space.md }}>
        A CSV, XLS or XLSX with a header row. The lot column is required (<em>Lot</em>, <em>Lot #</em>,
        <em> Lot Number</em> or <em>Lot Code</em>); any of Phase, Address, Owner, Owner Email, Builder,
        Builder Email, Acres and Notes are read when present. A lot already on file is matched on its
        code and only its non-empty cells are applied — a blank cell never clears anything, and lots
        missing from the sheet are left as they are.
      </div>
      <input type="file" accept=".csv,.xls,.xlsx,text/csv"
             onChange={e => pick(e.target.files && e.target.files[0])} disabled={busy} />
      {busy && <div style={{ marginTop: space.md }}><Spinner /></div>}
      {err && <div style={{ marginTop: space.md }}><ErrorMsg msg={err} /></div>}

      {shown && (
        <div style={{ marginTop: space.lg }}>
          <div style={{ display: 'flex', gap: space.md, flexWrap: 'wrap', marginBottom: space.md }}>
            <KpiCard label={result ? 'Added' : 'New lots'} value={result ? result.created : shown.toCreate} />
            <KpiCard label={result ? 'Updated' : 'Lots that change'} value={result ? result.updated : shown.toUpdate} />
            <KpiCard label="Already up to date" value={shown.unchanged} />
            <KpiCard label="Rows skipped" value={(shown.warnings || []).length} />
          </div>
          <div style={{ fontSize: font.xs, color: B.textMd, marginBottom: space.sm }}>
            Header found on row {shown.headerRow}; reading {(shown.columns || []).map(c => LOT_FIELD_LABELS[c] || c).join(', ')}.
            {(shown.ignoredColumns || []).length > 0 && <> Not imported: {shown.ignoredColumns.join(', ')}.</>}
          </div>

          {result && (result.failures || []).length > 0 && (
            <Section title="Could not be written" tone={B.red}>
              {result.failures.map(f => <div key={`f${f.row}`}>Row {f.row} · lot {f.lotCode}: {f.message}</div>)}
            </Section>
          )}
          {(shown.warnings || []).length > 0 && (
            <Section title="Rows and cells not used" tone="#8a6100">
              {shown.warnings.map((w, i) => <div key={`w${i}`}>Row {w.row}: {w.message}</div>)}
            </Section>
          )}
          {!result && (shown.create || []).length > 0 && (
            <Section title={`New lots${shown.toCreate > shown.create.length ? ` (first ${shown.create.length} of ${shown.toCreate})` : ''}`}>
              {shown.create.map(l => (
                <div key={`c${l.row}`}>Lot {l.lotCode}{[l.address, l.ownerName].filter(Boolean).length ? ` — ${[l.address, l.ownerName].filter(Boolean).join(' · ')}` : ''}</div>
              ))}
            </Section>
          )}
          {!result && (shown.update || []).length > 0 && (
            <Section title={`Lots that change${shown.toUpdate > shown.update.length ? ` (first ${shown.update.length} of ${shown.toUpdate})` : ''}`}>
              {shown.update.map(u => (
                <div key={`u${u.row}`}>Lot {u.lotCode}: {Object.entries(u.changes).map(([k, v]) => `${LOT_FIELD_LABELS[k] || k} → ${v}`).join('; ')}</div>
              ))}
            </Section>
          )}
          {nothingToDo && !result && <Empty>Every lot in this file is already on file with these details.</Empty>}
        </div>
      )}

      <ModalFooter>
        <Btn variant="secondary" onClick={onClose}>{result ? 'Close' : 'Cancel'}</Btn>
        {!result && (
          <Btn onClick={run} loading={busy} disabled={!preview || nothingToDo || busy || !file}>
            {preview ? `Import ${preview.toCreate + preview.toUpdate} lot${preview.toCreate + preview.toUpdate === 1 ? '' : 's'}` : 'Import'}
          </Btn>
        )}
      </ModalFooter>
    </Modal>
  );
}
function Section({ title, tone, children }) {
  return (
    <div style={{ marginBottom: space.md }}>
      <div style={{ fontSize: font.sm, fontWeight: weight.semibold, color: tone || B.text, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: font.xs, color: B.textMd, maxHeight: 180, overflowY: 'auto',
                    border: `1px solid ${B.border}`, borderRadius: radius.md, padding: space.sm }}>
        {children}
      </div>
    </div>
  );
}

// The inbox: submissions nobody could place. Mirrors the Email Inbox — a
// submission whose lot could not be determined waits for a person rather than
// being filed on a guess.
function InboxTab({ community, canWrite, onChanged }) {
  const { toast } = useToast();
  const [assigning, setAssigning] = useState(null);
  const [fetching, setFetching] = useState(null);
  const { data, loading, error, reload } = useApi(
    () => api.get(`arc/submissions?communityId=${community.communityID}&unassigned=1`), [community.communityID]);
  const lots = useApi(() => api.get(`arc/communities/${community.communityID}/lots`), [community.communityID]);

  if (loading) return <Spinner />;
  if (error) return <ErrorMsg msg={String(error)} />;
  const submissions = (data && data.submissions) || [];
  if (!submissions.length) {
    return <Card><Empty>Nothing is waiting — every submission has been placed on a lot.</Empty></Card>;
  }

  const autoOn = !!community.autoReview;

  const lotPickerItems = ((lots.data && lots.data.lots) || []).map(l => ({
    value: l.lotID,
    label: [`Lot ${l.lotCode}`, l.address, l.ownerName, l.builderName].filter(Boolean).join(' · '),
  }));

  const assign = async (submissionID, lotId) => {
    try {
      await api.post(`arc/submissions/${submissionID}/assign`, { lotId });
      // Filing is what makes an unmatched submission eligible, so say so —
      // otherwise nothing appears to happen and somebody files it twice.
      toast.success(autoOn ? 'Filed on the lot — the review will run shortly.' : 'Filed on the lot.');
      setAssigning(null); reload(); onChanged && onChanged();
    } catch (e) { toast.error(e.message || 'Could not file it'); }
  };

  return (
    <>
      {submissions.map(s => (
        <Card key={s.submissionID} style={{ marginBottom: space.sm }}>
          <div style={{ fontWeight: weight.semibold, color: B.text }}>{s.subject || '(no subject)'}</div>
          <div style={{ fontSize: font.xs, color: B.textMd, marginTop: 4 }}>
            {s.submittedBy || s.fromAddress || 'unknown sender'}
            {s.receivedUTC ? ` · ${fmt.date(s.receivedUTC)}` : ''}
            {` · ${s.docCount} document${s.docCount === 1 ? '' : 's'}`}
          </div>
          {s.bodyPreview && (
            <div style={{ fontSize: font.sm, color: B.textMd, marginTop: 6,
                          maxHeight: 60, overflow: 'hidden' }}>{s.bodyPreview}</div>
          )}
          {canWrite && (
            <div style={{ marginTop: space.sm }}>
              {assigning === s.submissionID ? (
                <div style={{ display: 'flex', gap: space.sm, alignItems: 'flex-start' }}>
                  {/* A search, not a list: a community holds hundreds of lots. The
                      label carries address, owner and builder so any of them finds it. */}
                  <div style={{ flex: 1 }}>
                    <InlineSearchPicker items={lotPickerItems} value=""
                      match={(i, q) => matchesAllWords(i.label, q)}
                      onChange={v => v && assign(s.submissionID, v)}
                      placeholder="Search lot, address, owner or builder…" clearLabel="— Choose a lot —" />
                  </div>
                  <Btn size="sm" variant="secondary" onClick={() => setAssigning(null)}>Cancel</Btn>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: space.sm, flexWrap: 'wrap' }}>
                  <Btn size="sm" onClick={() => setAssigning(s.submissionID)}>File on a lot</Btn>
                  {!s.docCount && s.source === 'email' && (
                    <Btn size="sm" variant="secondary" loading={fetching === s.submissionID}
                         onClick={async () => { setFetching(s.submissionID); await refetchAttachments(s.submissionID, toast); setFetching(null); reload(); }}>
                      Fetch attachments from email
                    </Btn>
                  )}
                </div>
              )}
            </div>
          )}
        </Card>
      ))}
    </>
  );
}

function LotModal({ communityId, onClose, onSaved }) {
  const { toast } = useToast();
  const [f, setF] = useState({ lotCode: '', address: '', ownerName: '', ownerEmail: '', builderName: '', builderEmail: '', phase: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setF(p => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`arc/communities/${communityId}/lots`, f);
      toast.success(`Lot ${f.lotCode} added.`);
      onSaved();
    } catch (e) { toast.error(e.message || 'Could not add the lot'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="Add a lot" onClose={onClose} width={560}>
      <Input label="Lot" value={f.lotCode} onChange={set('lotCode')} required placeholder="12, 12A, Blk 3 Lot 7" />
      <Input label="Address" value={f.address} onChange={set('address')} />
      <Input label="Phase" value={f.phase} onChange={set('phase')} />
      <Input label="Owner" value={f.ownerName} onChange={set('ownerName')} />
      {/* The owner's and builder's addresses are how an email with no lot
          number in its subject still reaches the right file. */}
      <Input label="Owner email" value={f.ownerEmail} onChange={set('ownerEmail')} type="email" />
      <Input label="Builder" value={f.builderName} onChange={set('builderName')} />
      <Input label="Builder email" value={f.builderEmail} onChange={set('builderEmail')} type="email" />
      <Textarea label="Notes" value={f.notes} onChange={set('notes')} />
      <ModalFooter>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        <Btn onClick={save} loading={saving} disabled={!f.lotCode.trim()}>Add lot</Btn>
      </ModalFooter>
    </Modal>
  );
}

// Mirrors ARC_TAG_RE in api/src/arcReview.js — the one gate every inbound
// message passes through, on EITHER address form. A tag failing it can never
// receive mail: Exchange delivers it fine (to import@, or to a bare alias IT
// set up) and ingestArcMessage just answers "not-arc" and drops it, silently.
// This is a client-side hint only — the server is the real, final check.
const ARC_TAG_PATTERN = /^arc(?:[-.].+)?$/i;

function NewCommunityModal({ onClose, onSaved, toast, existingTags = [] }) {
  const [f, setF] = useState({ name: '', intakeTag: 'arc' });
  const [saving, setSaving] = useState(false);
  const tagTrimmed = f.intakeTag.trim().toLowerCase();
  const tagTaken = existingTags.some(t => t.intakeTag === tagTrimmed);
  const tagBad = tagTrimmed !== '' && !ARC_TAG_PATTERN.test(tagTrimmed);
  const save = async () => {
    setSaving(true);
    try {
      await api.post('arc/communities', f);
      toast.success(`${f.name} created.`);
      onSaved();
    } catch (e) { toast.error(e.message || 'Could not create the community'); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="New community" onClose={onClose} width={520}>
      <Input label="Name" value={f.name} onChange={v => setF(p => ({ ...p, name: v }))} required />
      <Input label="Intake tag" value={f.intakeTag} onChange={v => setF(p => ({ ...p, intakeTag: v }))} />
      <div style={{ fontSize: font.xs, color: (tagTaken || tagBad) ? '#b3261e' : B.textMd, marginTop: -6, marginBottom: space.sm }}>
        Mail to <code>import+{f.intakeTag || 'arc'}@harvus.com</code> is filed against this community.
        A tag naming no community is parked rather than guessed onto another one.
        {tagBad && ' Must be "arc", or "arc" followed by "-" or "." and more (e.g. "arc-lr") — anything else can never receive mail.'}
        {tagTaken && ' This tag is already taken — see below.'}
      </div>
      {existingTags.length > 0 && (
        // What's already in use, so a conflict is visible before Create,
        // not discovered from a 409 after it.
        <div style={{ fontSize: font.xs, color: B.textMd, marginBottom: space.md }}>
          Tags already in use: {existingTags.map(t => `${t.intakeTag} (${t.name})`).join(', ')}
        </div>
      )}
      <ModalFooter>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        <Btn onClick={save} loading={saving} disabled={!f.name.trim() || tagBad}>Create</Btn>
      </ModalFooter>
    </Modal>
  );
}

// A community created before this check landed can carry an intake tag that
// ARC_TAG_RE rejects — "arclr" (no separator after "arc") never routes, and
// nothing ever notices, because Exchange delivers it fine and ingestArcMessage
// just answers "not-arc". This is the only way to fix a tag already on a live
// community; `PUT /api/arc/communities/{cid}` is the same tagFormatError gate
// as create, just partial (an absent field is left untouched).
function EditCommunityModal({ community, onClose, onSaved, toast, existingTags = [] }) {
  const [f, setF] = useState({ name: community.name, intakeTag: community.intakeTag || '' });
  // The deal a community's documents live on. Held as the picker's
  // `deal:<id>` value; only sent when it changed, so an untouched save never
  // rewrites DealID/DealDetailID (PUT writes only the fields the body carries).
  const initialDeal = community.dealID ? `deal:${community.dealID}` : '';
  const [deal, setDeal] = useState(initialDeal);
  const { dealPickerItems } = useDealPickerItems();
  const dealOnlyItems = useMemo(() => dealPickerItems.filter(i => !i.dealDetailID), [dealPickerItems]);
  const [saving, setSaving] = useState(false);
  const tagTrimmed = f.intakeTag.trim().toLowerCase();
  const tagTaken = existingTags.some(t => t.intakeTag === tagTrimmed);
  const tagBad = tagTrimmed !== '' && !ARC_TAG_PATTERN.test(tagTrimmed);
  const save = async () => {
    setSaving(true);
    try {
      const body = { name: f.name, intakeTag: f.intakeTag };
      if (deal !== initialDeal) {
        // A new deal invalidates any detail recorded against the old one.
        body.dealId = deal ? deal.replace(/^deal:/, '') : null;
        body.dealDetailId = null;
      }
      await api.put(`arc/communities/${community.communityID}`, body);
      toast.success(`${f.name} updated.`);
      onSaved();
    } catch (e) { toast.error(e.message || 'Could not update the community'); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Edit community" onClose={onClose} width={520}>
      <Input label="Name" value={f.name} onChange={v => setF(p => ({ ...p, name: v }))} required />
      <Input label="Intake tag" value={f.intakeTag} onChange={v => setF(p => ({ ...p, intakeTag: v }))} />
      <div style={{ fontSize: font.xs, color: (tagTaken || tagBad) ? '#b3261e' : B.textMd, marginTop: -6, marginBottom: space.sm }}>
        Mail to <code>import+{f.intakeTag || 'arc'}@harvus.com</code> is filed against this community.
        {tagBad && ' Must be "arc", or "arc" followed by "-" or "." and more (e.g. "arc-lr") — anything else can never receive mail, including what this community carries now.'}
        {tagTaken && ' This tag is already taken — see below.'}
      </div>
      <InlineSearchPicker label="Deal" items={dealOnlyItems} value={deal}
        onChange={setDeal} placeholder="Search deals…" />
      <div style={{ fontSize: font.xs, color: B.textMd, marginTop: -6, marginBottom: space.sm }}>
        The deal this community&rsquo;s governing documents and emailed plans are stored on.
        {deal && (
          <> <button type="button" onClick={() => setDeal('')}
                     style={{ background: 'none', border: 'none', padding: 0, color: B.navy, cursor: 'pointer', fontSize: font.xs, textDecoration: 'underline' }}>
            Unlink
          </button></>
        )}
      </div>
      {existingTags.length > 0 && (
        <div style={{ fontSize: font.xs, color: B.textMd, marginBottom: space.md }}>
          Tags already in use elsewhere: {existingTags.map(t => `${t.intakeTag} (${t.name})`).join(', ')}
        </div>
      )}
      <ModalFooter>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        <Btn onClick={save} loading={saving} disabled={!f.name.trim() || tagBad}>Save</Btn>
      </ModalFooter>
    </Modal>
  );
}
